import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errorMessage } from "@/lib/api";
import { AuthShell, AuthField, SubmitButton } from "@/components/AuthShell";
import { ServicePicker } from "@/components/services/ServicePicker";
import { StateSelect } from "@/components/StateSelect";

const EMPTY = { company_name: "", email: "", password: "", city: "", state: "", service_area: "", accept_terms: false, services: [] };

const TermsBox = ({ checked, onChange }) => (
  <label className="flex items-start gap-3 rounded-xl border-2 border-slate-200 bg-white p-3.5 text-sm leading-snug text-slate-700" data-testid="register-terms-label">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid="register-terms-checkbox"
      className="mt-0.5 h-5 w-5 shrink-0 accent-slate-900" />
    <span>
      I agree to the <Link to="/terms" target="_blank" rel="noopener noreferrer" className="font-semibold underline" data-testid="register-terms-link">Terms of Service</Link> and{" "}
      <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="font-semibold underline" data-testid="register-privacy-link">Privacy Policy</Link>.
      I understand AI price ranges are preliminary estimates, not quotes.
    </span>
  </label>
);

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const next = (e) => {
    e.preventDefault();
    if (form.password.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (!form.city.trim() || !form.state) { setError("Add the city and state where your business is based"); return; }
    if (!form.accept_terms) { setError("Please accept the Terms of Service and Privacy Policy"); return; }
    setError(""); setStep(2); window.scrollTo({ top: 0 });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.services.length === 0) { setError("Select at least one service you offer"); return; }
    setLoading(true); setError("");
    try { await register({ ...form, service_area: form.service_area.trim() || null }); navigate("/dashboard", { replace: true }); }
    catch (err) { setError(errorMessage(err)); setStep(1); }
    finally { setLoading(false); }
  };

  const footer = <>Already have an account? <Link to="/login" className="font-semibold text-slate-900 underline" data-testid="register-login-link">Log in</Link></>;

  if (step === 2) return (
    <AuthShell title="What services do you offer?" sub="Pick everything you do. Homeowners will only see these on your quote link." footer={footer}>
      <form onSubmit={submit} className="space-y-6" data-testid="register-services-form">
        <ServicePicker services={form.services} onChange={(services) => setForm({ ...form, services })} />
        {error && <p className="text-sm text-red-600" data-testid="register-error">{error}</p>}
        <div className="flex gap-2">
          <button type="button" onClick={() => setStep(1)} aria-label="Back" data-testid="register-back-button"
            className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border-2 border-slate-200 bg-white"><ArrowLeft className="h-5 w-5" /></button>
          <div className="flex-1"><SubmitButton loading={loading} testId="register-submit-button">Create account</SubmitButton></div>
        </div>
      </form>
    </AuthShell>
  );

  return (
    <AuthShell title="Start your free trial" sub="14 days free after you confirm your email, then $99/month. Cancel anytime." footer={footer}>
      <form onSubmit={next} className="space-y-4" data-testid="register-form">
        <AuthField label="Company name" required value={form.company_name} onChange={set("company_name")} data-testid="register-company-input" />
        <AuthField label="Email" type="email" autoComplete="email" required value={form.email} onChange={set("email")} data-testid="register-email-input" />
        <AuthField label="Password" type="password" autoComplete="new-password" required value={form.password} onChange={set("password")}
          placeholder="At least 8 characters" data-testid="register-password-input" />
        <div className="grid grid-cols-[1fr_130px] gap-3">
          <AuthField label="City" required autoComplete="address-level2" value={form.city} onChange={set("city")} placeholder="Cherry Hill" data-testid="register-city-input" />
          <label className="block">
            <span className="text-sm font-semibold text-slate-700">State</span>
            <StateSelect value={form.state} onChange={(state) => setForm({ ...form, state })} data-testid="register-state-select"
              className="mt-1.5 h-14 rounded-xl border-2 border-slate-200 bg-white px-3 text-[17px] focus:border-slate-900 focus:outline-none" />
          </label>
        </div>
        <AuthField label="Service area (optional)" value={form.service_area} onChange={set("service_area")} maxLength={120}
          placeholder="e.g. Camden & Burlington counties" data-testid="register-service-area-input" />
        <TermsBox checked={form.accept_terms} onChange={(accept_terms) => setForm({ ...form, accept_terms })} />
        {error && <p className="text-sm text-red-600" data-testid="register-error">{error}</p>}
        <SubmitButton loading={false} testId="register-next-button">Continue</SubmitButton>
        <p className="text-center text-xs text-slate-500">Step 1 of 2 · Next: choose your services</p>
      </form>
    </AuthShell>
  );
}
